const leaveTypeTooltips = {
  name: "Full leave type name shown to admins and employees.",
  short_name: "Short code/name used where space is limited, such as rosters and lists.",
  font_color: "Display color used to identify this leave type in calendars, rosters, and reports.",
  is_active: "Controls whether this leave type can be selected and assigned.",
  is_auto_allotment: "Automatically creates employee leave quota for this type during allotment.",
  balance_on_joining: "Allots the joining-month balance from the employee's date of joining instead of a tax structure.",
  tax_structure_id: "Tax structure used to calculate leave quota when joining-month balance is not based on DOJ.",
  is_composite: "Combines multiple leave types and deducts quota from the configured participant leave types.",
  is_short: "Enables short leave slabs so employees can apply for partial-shift leave.",
  is_deductible: "Marks this leave as quota-deductible when requests are approved.",
  sandwich: "Includes qualifying off days between or around leaves in the total leave count.",
  splitable: "Allows employees to apply for fewer days than the full accumulative count.",
  prorated: "Prorates quota based on employment period instead of giving the full tenure balance.",
  add_earned_quota: "Adds earned quota into the employee's leave balance.",
  is_back_date_allowed: "Allows employees to apply for dates before today, subject to the back-date limit.",
  days_back_date_allowed: "Maximum number of past days allowed for this leave type.",
  is_attachment_required: "Requires an attachment when the leave request reaches the configured day threshold.",
  days_attachment_required: "Minimum leave duration after which an attachment is required.",
  encashment: "Allows unused carry-forward quota to be encashed when encashment rules run.",
  encashment_min: "Minimum carry-forward quota required before encashment is allowed.",
  encashment_max: "Maximum quota that can be encashed for this leave type.",
  carry_forward: "Allows unused balance to move into the next leave tenure.",
  carry_forward_min: "Minimum unused balance required before carry forward applies.",
  carry_forward_max: "Maximum unused balance that can be carried forward.",
  carry_forward_years: "Number of years the carried-forward balance remains valid.",
  gender_id: "Limits this leave type to a gender; All keeps it available for everyone.",
  can_apply_in_probation: "Allows employees in probation to apply for this leave type.",
  min_employee_experience: "Minimum employment experience in months required before applying.",
  max_applications_in_month: "Maximum number of applications allowed for this leave type within a single month.",
  max_applications_in_employment: "Maximum number of applications allowed for this leave type during employment.",
  gap_between_applications: "Required gap in months between two applications for this leave type.",
  tenure_id: "Tenure used to calculate quota and availability for this leave type.",
  acc_count: "Total quota/count allotted for this leave type in its tenure.",
  has_max_limit: "Limits how many leave days can be applied within a selected tenure.",
  limit_tenure_id: "Tenure window used to enforce the maximum application limit.",
  amount_apply_in_tenure: "Maximum leave count allowed within the selected limit tenure.",
  min_days_per_leave: "Minimum days allowed per request when splitable is enabled.",
  max_days_per_leave: "Maximum days allowed per request when splitable is enabled.",
  can_apply_for_remaining: "Allows applying below the minimum when only the remaining quota is less than the minimum.",
  leave_type_id: "Leave type included in this composite setting.",
  quota: "Quota consumed by this short leave slab.",
  type: "Defines whether the slab value is percentage-based or flat.",
  value: "Deduction value applied by the short leave slab.",
  short_leave_slab_name: "HR-friendly slab name employees will choose when applying short leave, such as Half Day or Two Hours.",
  short_leave_slab_quota: "Number of leave days or units deducted from the employee's quota when this slab is used.",
  short_leave_slab_type: "Choose Percentage when the slab is based on shift length, or Flat when it always deducts a fixed value.",
  short_leave_slab_value: "The percentage or fixed value used to calculate the short leave duration or deduction."
};

const leaveRequestTooltips = {
  employment_id: "Employee for whom this leave request will be created.",
  leave_type_id: "Leave type to apply; its rules decide quota, limits, back-date allowance, and short leave options.",
  current_available: "Current available balance for the selected leave type and employee.",
  leave_type_available: "Balance still available in this leave type when the request was made.",
  is_short: "Apply this request as a short leave using the configured short leave slabs.",
  short_leave_type_slab_id: "Short leave slab that controls the quota/count deducted for this request.",
  start: "First leave date. Backend checks quota year, back-date rules, payroll closure, and shift availability.",
  end: "Last leave date. Disabled for short leave because short leave applies to one selected shift/date.",
  time_start: "Start time for a short leave request.",
  time_end: "Auto-calculated end time for short leave based on shift duration and slab count.",
  shift_count: "Number of selected working shifts included in the leave request.",
  sandwich_count: "Off days added to leave count when sandwich rules apply around selected shifts.",
  count: "Total leave count deducted from quota, including selected shifts and sandwich count.",
  reason: "Reason submitted with the leave request for approval workflow and history.",
  attachment: "Optional supporting image; some leave types may require it based on request duration.",
  leave_type_ids: "Leave types to assign to the selected employees; composite leave types also apply their participant types."
};

const relaxationTooltips = {
  employment_id: "Employee this relaxation is for. Their approval workflow decides who receives the request.",
  date: "The shift date the relaxation applies to. It must be a day the employee has a shift.",
  start_time: "Time the employee is allowed to start instead of the scheduled shift start. Once approved, the minutes between this and the shift start are treated as relaxation, not late arrival.",
  end_time: "Time the employee is allowed to finish instead of the scheduled shift end. Once approved, the minutes between this and the shift end are treated as relaxation, not early leaving.",
  reason: "Why the relaxation is needed. Approvers read this before approving.",
  remarks: "Remarks added while approving or rejecting. They are visible in the request history.",
  last_remarks: "Remarks written by the last approver. Visible in the request history.",
  description: "Additional detail kept with the request.",
};

const leaveApprovalTooltips = {
  leave_type_name: "Leave type the employee applied for. Its rules decide the quota deducted and whether an attachment is required.",
  start: "First day of the requested leave.",
  end: "Last day of the requested leave.",
  count: "Total leave days that will be deducted from the employee's quota if you approve, including any sandwich days.",
  reason: "The reason the employee gave for the leave.",
  created_at: "When the employee submitted this request. Useful for checking whether it was applied in advance.",
  reasons: "Reasons recorded at each step of the approval so far.",
  description: "Additional detail the employee added to the request.",
  leave_type_total: "The employee's total quota for this leave type in the current leave year.",
  leave_type_available: "Quota still available. Approving a request larger than this pushes the balance negative, which the attendance leave policy will later deduct from salary.",
  leave_type_availed: "Quota the employee has already used from this leave type this year.",
  last_remarks: "Remarks written by the last approver. Visible in the request history.",
};

const quotaAdjustTooltips = {
  leave_type_name: "Leave type whose balance is being adjusted. Read-only.",
  fiscal_year_start: "First day of the leave year this balance belongs to. Adjusting the wrong year changes the wrong balance.",
  fiscal_year_end: "Last day of the leave year this balance belongs to.",
  quota: "Quota allotted for this leave year, before carry forward. Changing it changes what the employee can apply for.",
  total_quota: "Allotted quota plus carry forward. This is the figure available balance is worked out from.",
  availed: "Days already used from this leave type. Raising it reduces the available balance immediately.",
  available: "Days still left to apply. It is worked out from the total quota and what has been used, so correct the quota or availed figures rather than this.",
  carry_forward_quota: "Unused days brought forward from the previous leave year, within the leave type's carry-forward limits.",
  encashed_quota: "Days already converted into money through leave encashment. They are no longer available to apply.",
};

const leaveConfigurationTooltips = {
  is_applicable_start: "Allow employees to request a relaxation for arriving after the shift start.",
  start_duration_limit: "The most minutes after shift start an employee may request as relaxation. Requests beyond this are not allowed.",
  is_applicable_end: "Allow employees to request a relaxation for leaving before the shift end.",
  end_duration_limit: "The most minutes before shift end an employee may request as relaxation. Requests beyond this are not allowed.",
  leave_type_id: "Leave type that receives compensatory leave earned from overtime. Choose the type employees should apply against when using CPL.",
  cpl_limit: "Maximum compensatory leave balance an employee may hold. When payroll closes, any CPL beyond this is not added, so the balance stops at this number.",
  leave_type_deductible: "When ticked, this leave type's balance is used to absorb attendance deductions before salary is deducted. Untick it to keep the balance for leave only.",
};

const consentTooltips = {
  employment_id: "Colleague you want an opinion from before this request moves forward. They receive it as a consent request.",
  message: "What you want to ask them. They see this text with the request.",
};

export const getLeaveTypeTooltip = (key) => leaveTypeTooltips[key];
export const getLeaveRequestTooltip = (key) => leaveRequestTooltips[key];

export const leaveTooltips = {
  leaveType: leaveTypeTooltips,
  leaveRequest: leaveRequestTooltips,
  relaxation: relaxationTooltips,
  leaveApproval: leaveApprovalTooltips,
  quotaAdjust: quotaAdjustTooltips,
  configuration: leaveConfigurationTooltips,
  consent: consentTooltips,
};
